import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground-950 mb-2">分类未找到</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  return (
    <>
      <SEOHead
        title={`${category.name} - 摄影教程分类`}
        description={category.description}
        keywords={`${category.name},自拍偷拍,摄影教程,${category.name}教程`}
        canonicalUrl={`/category/${slug}`}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100">
                <i className={`${category.icon} text-lg text-accent-700`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                {category.name}
              </h1>
            </div>
            <p className="text-foreground-600 text-sm max-w-2xl">
              {category.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10 pb-8 border-b border-background-200">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                      <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium">
                        {article.tags[0]}
                      </span>
                      <span>{article.readTime} 分钟阅读</span>
                      <span>{article.date}</span>
                    </div>
                    <h2 className="text-base font-semibold text-foreground-950 font-heading mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-file-list-3-line text-4xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}